import React, { forwardRef, useImperativeHandle } from 'react';
import { i18n } from 'src/common';
import { Text, InputStreet, InputCity, Card } from 'src/components';
import { InputText } from 'src/components/inputs/inputText.input';
import { FieldConfig, Form, FormRef } from './form';
import { Validation } from './validation';

export interface AddressData {
  city?: string;
  cityKompareId?: string;
  cityParentName?: string;
  street?: string;
  streetKompareId?: string;
  houseNumber?: string;
  zipCode?: number | string;
}

interface AddressFormProps {
  data: AddressData;
  onSubmit?: (data: AddressData) => void;
}

export const AddressForm = forwardRef<FormRef, AddressFormProps>(
  ({ data, onSubmit }, ref) => {
    const formRef = React.useRef<FormRef>(null);

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (formRef.current) {
          formRef.current.submitForm();
        }
      },
      getValues: () => formRef.current?.getValues?.() || {},
      validateForm: () => formRef.current?.validateForm?.() ?? false,
      setFieldValue: (fieldName: string, value: unknown, validation?: boolean) => {
        formRef.current?.setFieldValue?.(fieldName, value, validation);
      },
    }));

    const fields: FieldConfig<AddressData>[] = [
      {
        name: 'city',
        label: i18n.t('components.forms.address.city'),
        component: InputCity,
        componentProps: { cityKompareId: 'cityKompareId' },
        validations: [{ type: 'required', message: i18n.t('components.forms.address.cityRequired') }] as Validation[],
        formatValue: (values: any) => (values.city || values.zipCode) && `${values.city}, ${values.zipCode}`,
      },
      {
        name: 'street',
        label: i18n.t('components.forms.address.street'),
        component: InputStreet,
        componentProps: { cityKompareId: 'streetKompareId' },
        validations: [{ type: 'required', message: i18n.t('components.forms.address.streetRequired') }] as Validation[],
      },
      {
        name: 'houseNumber',
        label: i18n.t('components.forms.address.houseNumber'),
        component: InputText,
        validations: [{ type: 'required', message: i18n.t('components.forms.address.houseNumberRequired') }] as Validation[],
      },
    ];

    const handleSubmit = (values: Record<string, unknown>) => {
      const raw = values as Partial<AddressData>;
      const zipNum = raw.zipCode != null
        ? (typeof raw.zipCode === 'string' ? parseInt(raw.zipCode, 10) : raw.zipCode)
        : undefined;
      const convertedValues: AddressData = {
        ...raw,
        zipCode: zipNum,
      };
      onSubmit?.(convertedValues);
    };

    return (
      <Card padding="24px 24px 32px 24px" title={<Text type="medium" bold>{i18n.t('components.forms.address.locationTitle')}</Text>}>
        <Form
          ref={formRef}
          fields={fields}
          initialValues={data}
          onSubmit={handleSubmit}
          config={{ gap: 32 }}
        />
      </Card>
    );
  }
);
